import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { ClientLayout } from '../../layouts/ClientLayout';
import ClientHome from './ClientHome';
import ClientProgress from './ClientProgress';
import GoalManagement from '../shared/GoalManagement';
import ProfilePage from './ProfilePage';
import { ClientIntegrations } from './ClientIntegrations';
import { useAuth } from '../../contexts/AuthContext';
import { useClientOnboarding } from '../../hooks/useClientOnboarding';
import WelcomeModal from '../WelcomeModal';

/**
 * ClientNewViews - Router for new client dashboard views
 *
 * Wraps new views in ClientLayout with:
 * - Consolidated 6-item nav (Home, Progress, Goals, Documents, Profile, Settings)
 * - Blue "New View" banner with switch to legacy
 * - Routes to individual page components
 *
 * New pages are built progressively:
 * - Home: ClientHome (NEW)
 * - Progress: ClientProgress (NEW - calendar + placeholder tiles)
 * - Goals: GoalManagement (existing, reused)
 * - Documents: placeholder (TODO)
 * - Profile: ProfilePage (existing, reused)
 * - Settings: ClientIntegrations (existing, reused)
 */
const ClientNewViews: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Onboarding and modal state
  const {
    isFirstLogin,
    needsProfileCompletion,
    isLoading: onboardingLoading,
    skipProfileCompletion
  } = useClientOnboarding(user);

  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'welcome' | 'incomplete'>('welcome');
  const [modalShownThisSession, setModalShownThisSession] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  // Welcome modal / profile nag logic
  useEffect(() => {
    if (user?.id && !onboardingLoading) {
      const profileModalShown = sessionStorage.getItem('profileModalShown');
      const wasModalShown = profileModalShown === 'true';

      if (modalShownThisSession || wasModalShown) {
        return;
      }

      if (isFirstLogin) {
        setModalType('welcome');
        setShowModal(true);
        setModalShownThisSession(true);
        sessionStorage.setItem('profileModalShown', 'true');
      } else if (needsProfileCompletion && !isFirstLogin) {
        setModalType('incomplete');
        setShowModal(true);
        setModalShownThisSession(true);
        sessionStorage.setItem('profileModalShown', 'true');
      }
    }
  }, [user?.id, onboardingLoading, isFirstLogin, needsProfileCompletion, modalShownThisSession]);

  // Extract current view from path
  const pathSegments = location.pathname.split('/').filter(Boolean);
  // /client/goals -> ['client', 'goals'] -> 'goals'
  // /client -> ['client'] -> 'home'
  const currentView = pathSegments[1] || 'home';

  const handleNavigate = (view: string) => {
    navigate(`/client/${view}`);
  };

  const handleModalAccept = async () => {
    setShowModal(false);

    if (modalType === 'welcome') {
      try {
        await fetch(`/api/users/${user?.id}/mark-welcome-seen`, {
          method: 'POST',
          headers: getAuthHeaders(),
        });
      } catch (error) {
        console.error('Failed to mark welcome modal as seen:', error);
      }
    }

    setTimeout(() => {
      handleNavigate('profile');
    }, 100);
  };

  const handleModalClose = async () => {
    setShowModal(false);

    if (modalType === 'welcome') {
      try {
        await fetch(`/api/users/${user?.id}/mark-welcome-seen`, {
          method: 'POST',
          headers: getAuthHeaders(),
        });
      } catch (error) {
        console.error('Failed to mark welcome modal as seen:', error);
      }
    }

    skipProfileCompletion();
  };

  const renderContent = () => {
    switch (currentView) {
      case 'home':
        return (
          <ClientHome profileBanner={needsProfileCompletion ? (
            <div className="profile-incomplete-banner">
              <div className="banner-content">
                <div className="banner-icon">⚠️</div>
                <div className="banner-text">
                  <h3>Profile Incomplete</h3>
                  <p>Complete your profile to get started with Mission: Trimpossible</p>
                </div>
                <button
                  onClick={() => handleNavigate('profile')}
                  className="button button-primary"
                >
                  Complete Profile
                </button>
              </div>
            </div>
          ) : undefined} />
        );

      case 'progress':
        return (
          <div className="page-content">
            <ClientProgress />
          </div>
        );

      case 'goals':
        return (
          <div className="page-content">
            <GoalManagement readOnly={user?.self_managed === false} />
          </div>
        );

      case 'documents':
        return (
          <div className="page-content">
            <div className="page-header">
              <h1>Documents</h1>
            </div>
            <div className="placeholder-content">
              <p>Your documents and forms will appear here...</p>
              <span>Files from your coach, intake forms, and uploads.</span>
            </div>
          </div>
        );

      case 'profile':
        return <ProfilePage />;

      case 'settings':
        return (
          <div className="page-content">
            <div className="page-header">
              <h1>Settings</h1>
            </div>
            <ClientIntegrations />
          </div>
        );

      default:
        return (
          <div className="page-content">
            <div className="page-header">
              <h1>{currentView}</h1>
            </div>
            <div className="placeholder-content">
              <p>This section is coming soon...</p>
            </div>
          </div>
        );
    }
  };

  return (
    <ClientLayout currentView={currentView} onNavigate={handleNavigate}>
      <Routes>
        <Route path="/" element={renderContent()} />
        <Route path="/home" element={renderContent()} />
        <Route path="/progress" element={renderContent()} />
        <Route path="/goals" element={renderContent()} />
        <Route path="/documents" element={renderContent()} />
        <Route path="/profile" element={renderContent()} />
        <Route path="/settings" element={renderContent()} />
        <Route path="/*" element={renderContent()} />
      </Routes>

      {/* Welcome/Profile Incomplete Modal */}
      {showModal && user && (
        <WelcomeModal
          user={user}
          type={modalType}
          onAccept={handleModalAccept}
          onClose={handleModalClose}
        />
      )}

      <style>{`
        .profile-incomplete-banner {
          background: linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(245, 158, 11, 0.05));
          border: 1px solid rgba(245, 158, 11, 0.3);
          border-radius: var(--radius-medium);
          padding: 16px 20px;
          margin-bottom: 16px;
        }

        .profile-incomplete-banner .banner-content {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .profile-incomplete-banner .banner-icon {
          font-size: 24px;
          flex-shrink: 0;
        }

        .profile-incomplete-banner .banner-text {
          flex: 1;
        }

        .profile-incomplete-banner .banner-text h3 {
          margin: 0 0 4px;
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .profile-incomplete-banner .banner-text p {
          margin: 0;
          font-size: 14px;
          color: var(--text-secondary);
        }

        .profile-incomplete-banner .button-primary {
          background: var(--accent-blue);
          color: white;
          border: none;
          padding: 8px 20px;
          border-radius: var(--radius-medium);
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          white-space: nowrap;
          transition: opacity 0.2s ease;
        }

        .profile-incomplete-banner .button-primary:hover {
          opacity: 0.9;
        }

        .page-content {
          padding: 0;
          width: 100%;
        }

        .page-header {
          margin-bottom: 24px;
        }

        .page-header h1 {
          font-size: 28px;
          font-weight: 700;
          color: var(--text-primary);
          margin: 0;
        }

        .placeholder-content {
          text-align: center;
          padding: 48px 24px;
          background: var(--bg-primary);
          border: 1px dashed var(--border-secondary);
          border-radius: var(--radius-large);
          color: var(--text-tertiary);
        }

        .placeholder-content p {
          font-size: 16px;
          margin: 0 0 8px;
          color: var(--text-secondary);
        }

        .placeholder-content span {
          font-size: 14px;
        }

        @media (max-width: 768px) {
          .view-banner {
            flex-direction: column;
            text-align: center;
            gap: 8px;
          }
        }
      `}</style>
    </ClientLayout>
  );
};

export default ClientNewViews;
